/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl'

import { EuiBetaBadge, EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiText } from '@elastic/eui'

import type {
  DeploymentCreateRequest,
  ElasticsearchClusterTopologyElement,
  InstanceConfiguration,
} from '@modules/cloud-api/v1/types'
import CuiToggleablePopoverForClassComp from '@modules/cui/CuiToggleablePopoverForClassComp'
import type { AnyTopologyElement, SliderInstanceType } from '@modules/ui-types'

import TopologyPanel from '@/components/Topology/DeploymentTemplates/components/DeploymentInfrastructure/TopologyElement/TopologyPanel'
import {
  getInstanceConfigurationById,
  getInstanceConfigurationByIdAndVersion,
} from '@/lib/instanceConfigurations/instanceConfiguration'
import { getSliderDefinition } from '@/lib/sliders/definitions'
import {
  isAutoscaleableTier,
  isAutoscalingSupportedInTemplate,
} from '@/lib/stackDeployments/selectors/autoscaling'
import { hasOnlyPlanLevelSettings } from '@/lib/stackDeployments/selectors/configuration'
import {
  getNodeRoles,
  isData,
  isDedicatedML,
  isDedicatedMaster,
  isFrozen,
} from '@/lib/stackDeployments/selectors/nodeRoles'
import {
  getFirstSliderCluster,
  getSliderPlan,
  getSliderUserSettings,
} from '@/lib/stackDeployments/selectors/stackDeployment'

import { hashMatchesNodeConfiguration } from '../../../../../../lib/stackDeployments/topology'
import { getDefaultUserSettings } from '../../../../../../lib/stackDeployments/userSettings'
import ScrollIntoView from '../../../../../ScrollIntoView'
import Heading from '../TopologyElement/Heading'
import NormalizeSizing from '../TopologyElement/NormalizeSizing'
import SizePicker from '../TopologyElement/SizePicker'
import {
  makeAutoscalingMaxSizePickerOptions,
  makeAutoscalingMinSizePickerOptions,
  makeCurrentSizePickerOptions,
} from '../TopologyElement/SizePicker/helpers'
import Summary from '../TopologyElement/Summary'
import TopologyPanelRow from '../TopologyElement/TopologyPanelRow'
import UserSettings from '../TopologyElement/UserSettings'
import ZoneCount from '../TopologyElement/ZoneCount'
import { USER_SETTINGS_DOC_LINKS } from '../helpers'

import AutoscalingInfo from './AutoscalingInfo'
import SelectInstanceConfiguration from './SelectInstanceConfiguration'

import type { WrappedComponentProps } from 'react-intl'
import type { NormalizeSizingProps } from '../TopologyElement/NormalizeSizing'

export type AllProps = {
  id: string
  deployment: DeploymentCreateRequest
  sliderInstanceType: SliderInstanceType
  topologyElement: AnyTopologyElement
  instanceConfigurations: InstanceConfiguration[]
  onChange: undefined | ((path: string[], value: any) => void) // path is scoped to the topology element
  onPlanChange: undefined | ((path: string[], value: any) => void) // path is scoped to the plan (above topology element)
  onResourceChange: undefined | ((path: string[], value: any) => void) // path is scoped to the resource (above plan)
  maxZoneCount: number
  maxInstanceCountForEnvironment: number
  maxInstanceCount?: number
  isReadOnly?: boolean
  // optionally supply additional fields consisting of
  // TopologyElement-compatible components
  extraFields?: Array<React.ComponentType<Props>>
  templateICs: InstanceConfiguration[]
}

type Props = AllProps & WrappedComponentProps

class TemplateTopologyElement extends Component<Props> {
  render(): JSX.Element {
    const { topologyElement } = this.props

    return (
      <ScrollIntoView
        whenLocationMatches={(hash) => hashMatchesNodeConfiguration(hash, topologyElement)}
      >
        <div data-test-subj='topologyElement' data-test-topologyelementid={topologyElement.id}>
          <TopologyPanel headerContent={this.renderHeader()} footerContent={this.renderSummary()}>
            {this.renderInstanceConfiguration()}
            {this.renderInitialSize()}
            {this.renderMinSize()}
            {this.renderMaxSize()}
            {this.renderAvailabilityZones()}
            {this.renderExtraFields()}
            {this.renderUserSettings()}
          </TopologyPanel>
        </div>
      </ScrollIntoView>
    )
  }

  renderHeader(): JSX.Element | null {
    const {
      intl: { formatMessage },
      topologyElement,
      sliderInstanceType,
    } = this.props

    const messages = defineMessages({
      autoscaling: {
        id: 'instance-template-section-popover.autoscaling',
        defaultMessage: 'Supports autoscaling',
      },
      soonDeprecated: {
        id: 'instance-template-section-popover.deprecated-soon',
        defaultMessage: 'Soon to be deprecated',
      },
    })

    const betaBadges: JSX.Element[] = []

    if (isAutoscaleableTier({ topologyElement, version: undefined })) {
      betaBadges.push(
        <EuiBetaBadge key='autoscaling' label={formatMessage(messages.autoscaling)} />,
      )
    }

    if (sliderInstanceType === `apm`) {
      betaBadges.push(
        <CuiToggleablePopoverForClassComp
          key='apm'
          anchorPosition='upCenter'
          ownFocus={true}
          toggleButton={(toggleButtonFunc) => (
            <EuiBetaBadge
              label={formatMessage(messages.soonDeprecated)}
              data-test-id='instance-deprecating-badge'
              onClick={toggleButtonFunc}
            />
          )}
        >
          <FormattedMessage
            id='deployment-template-apm.deprecating'
            defaultMessage='APM will be replaced with Integrations Server in version 8.1.0'
          />
        </CuiToggleablePopoverForClassComp>,
      )
    }

    // The onChange props is overridden to undefined here because the only
    // reason <Heading /> needs it is to disable -- but you can't do that in a
    // template, only set the starting size to 0. Similarly, autoscaling is
    // always "enabled" in a template context.
    return (
      <Heading
        {...this.props}
        instanceConfiguration={this.getCurrentInstanceConfiguration()}
        onChange={undefined}
        betaBadges={betaBadges}
      />
    )
  }

  renderInstanceConfiguration() {
    const label = (
      <FormattedMessage
        id='deploymentTemplateInfrastructure-templateTopologyElement-instanceConfigLabel'
        defaultMessage='Instance configuration'
      />
    )

    return (
      <TopologyPanelRow wideLabel={true} alignItems='center' label={label}>
        <EuiFlexGroup responsive={false} gutterSize='s' alignItems='baseline'>
          <EuiFlexItem>
            <SelectInstanceConfiguration
              {...this.props}
              instanceConfiguration={this.getCurrentInstanceConfiguration()}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false} style={{ width: '2rem' }} />
        </EuiFlexGroup>
      </TopologyPanelRow>
    )
  }

  renderInitialSize(): JSX.Element | null {
    const {
      maxInstanceCountForEnvironment,
      deployment,
      topologyElement,
      sliderInstanceType,
      maxInstanceCount,
      intl: { formatMessage },
    } = this.props

    if ((topologyElement as ElasticsearchClusterTopologyElement).autoscaling_min) {
      return null // if we have min, we don't need initial
    }

    const instanceConfiguration = this.getCurrentInstanceConfiguration()

    const label = (
      <FormattedMessage
        id='deploymentTemplateInfrastructure-templateTopologyElement-initialSizeLabel'
        defaultMessage='Initial size <nowrap>per zone</nowrap>'
        values={{
          nowrap: (content) => <span style={{ whiteSpace: 'nowrap' }}>{content}</span>,
        }}
      />
    )

    const ariaLabel = formatMessage({
      id: 'deploymentTemplateInfrastructure-templateTopologyElement-initialSizeLabel.aria-label',
      defaultMessage: 'Initial size per zone',
    })

    const sliderNodeTypes = instanceConfiguration.node_types
    const isBlobStorage = false // don't want blob popovers to show even for frozen

    const controlRenderer = (normalizeSizingProps: NormalizeSizingProps) => {
      const options = makeCurrentSizePickerOptions({
        ...normalizeSizingProps,
        deployment: {
          ...deployment,
          // for template purposes, always regard autoscaling as possible
          settings: { ...deployment.settings, autoscaling_enabled: true },
        },
        topologyElement,
        sliderInstanceType,
        sliderNodeTypes,
        isBlobStorage,
        canShowZero: true,
      })

      return (
        <EuiFlexGroup gutterSize='xs' justifyContent='flexStart' alignItems='baseline'>
          <EuiFlexItem>
            <SizePicker
              data-test-id='topologyElement-currentSize'
              {...normalizeSizingProps}
              options={options}
              maxInstanceCount={
                maxInstanceCount
                  ? Math.min(maxInstanceCount, maxInstanceCountForEnvironment)
                  : maxInstanceCountForEnvironment
              }
              topologyElement={topologyElement}
              sliderInstanceType={sliderInstanceType}
              sliderNodeTypes={sliderNodeTypes}
              isBlobStorage={isBlobStorage}
              aria-label={ariaLabel}
            />{' '}
          </EuiFlexItem>
          <EuiFlexItem grow={false} style={{ width: '2rem' }}>
            <AutoscalingInfo topologyElement={topologyElement} />
          </EuiFlexItem>
        </EuiFlexGroup>
      )
    }

    return (
      <Fragment>
        <EuiSpacer size='m' />
        <TopologyPanelRow wideLabel={true} alignItems='center' label={label}>
          <NormalizeSizing
            {...this.props}
            instanceConfiguration={instanceConfiguration}
            autoscalingSizeFilter='fixToMin'
          >
            {controlRenderer}
          </NormalizeSizing>
        </TopologyPanelRow>
      </Fragment>
    )
  }

  renderMinSize(): JSX.Element | null {
    const {
      maxInstanceCountForEnvironment,
      topologyElement,
      maxInstanceCount,
      deployment,
      sliderInstanceType,
      intl: { formatMessage },
    } = this.props

    if (!isAutoscalingSupportedInTemplate({ deploymentTemplate: deployment })) {
      return null
    }

    if (!(topologyElement as ElasticsearchClusterTopologyElement).autoscaling_min) {
      return null
    }

    const instanceConfiguration = this.getCurrentInstanceConfiguration()

    const label = (
      <FormattedMessage
        id='deploymentTemplateInfrastructure-templateTopologyElement-minSizeLabel'
        defaultMessage='Minimum size <nowrap>per zone</nowrap>'
        values={{
          nowrap: (content) => <span style={{ whiteSpace: 'nowrap' }}>{content}</span>,
        }}
      />
    )

    const ariaLabel = formatMessage({
      id: 'deploymentTemplateInfrastructure-templateTopologyElement-minSizeLabel.aria-label',
      defaultMessage: 'Minimum size per zone',
    })

    const sliderNodeTypes = instanceConfiguration.node_types
    const isBlobStorage = false // don't want blob popovers to show even for frozen

    const controlRenderer = (normalizeSizingProps: NormalizeSizingProps) => {
      const options = makeAutoscalingMinSizePickerOptions({
        ...normalizeSizingProps,
        deployment: {
          ...deployment,
          // for template purposes, always regard autoscaling as possible
          settings: { ...deployment.settings, autoscaling_enabled: true },
        },
        topologyElement,
        sliderInstanceType,
        sliderNodeTypes,
        isBlobStorage,
        canShowZero: true,
      })

      return (
        <EuiFlexGroup gutterSize='xs' justifyContent='flexStart' alignItems='baseline'>
          <EuiFlexItem>
            <SizePicker
              data-test-id='topologyElement-autoscalingMinSize'
              {...normalizeSizingProps}
              options={options}
              maxInstanceCount={
                maxInstanceCount
                  ? Math.min(maxInstanceCount, maxInstanceCountForEnvironment)
                  : maxInstanceCountForEnvironment
              }
              topologyElement={topologyElement}
              sliderInstanceType={sliderInstanceType}
              sliderNodeTypes={sliderNodeTypes}
              isBlobStorage={isBlobStorage}
              // overrides to have SizePicker get/set `autoscaling_min` instead of `size`
              size={normalizeSizingProps.autoscalingMinSize || 0}
              onChangeSize={
                normalizeSizingProps.onChangeAutoscaling &&
                ((minValue) => normalizeSizingProps.onChangeAutoscaling!({ minValue }))
              }
              aria-label={ariaLabel}
            />{' '}
          </EuiFlexItem>
          <EuiFlexItem grow={false} style={{ width: '2rem' }} />
        </EuiFlexGroup>
      )
    }

    return (
      <Fragment>
        <EuiSpacer size='m' />
        <TopologyPanelRow wideLabel={true} alignItems='center' label={label}>
          <NormalizeSizing
            {...this.props}
            instanceConfiguration={instanceConfiguration}
            autoscalingSizeFilter='fixToMin'
          >
            {controlRenderer}
          </NormalizeSizing>
        </TopologyPanelRow>
      </Fragment>
    )
  }

  renderMaxSize(): JSX.Element | null {
    const {
      maxInstanceCountForEnvironment,
      topologyElement,
      maxInstanceCount,
      deployment,
      sliderInstanceType,
      intl: { formatMessage },
    } = this.props

    if (!isAutoscalingSupportedInTemplate({ deploymentTemplate: deployment })) {
      return null
    }

    if (!isData({ topologyElement }) && !isDedicatedML({ topologyElement })) {
      return null
    }

    const instanceConfiguration = this.getCurrentInstanceConfiguration()

    const label = (
      <FormattedMessage
        id='deploymentTemplateInfrastructure-templateTopologyElement-maxSizeLabel'
        defaultMessage='Maximum size <nowrap>per zone</nowrap>'
        values={{
          nowrap: (content) => <span style={{ whiteSpace: 'nowrap' }}>{content}</span>,
        }}
      />
    )

    const ariaLabel = formatMessage({
      id: 'deploymentTemplateInfrastructure-templateTopologyElement-maxSizeLabel.aria-label',
      defaultMessage: 'Maximum size per zone',
    })

    const sliderNodeTypes = instanceConfiguration.node_types
    const isBlobStorage = false // don't want blob popovers to show even for frozen

    const controlRenderer = (normalizeSizingProps: NormalizeSizingProps) => {
      const options = makeAutoscalingMaxSizePickerOptions({
        ...normalizeSizingProps,
        deployment: {
          ...deployment,
          // for template purposes, always regard autoscaling as possible
          settings: { ...deployment.settings, autoscaling_enabled: true },
        },
        topologyElement,
        sliderInstanceType,
        sliderNodeTypes,
        isBlobStorage,
        canShowZero: true,
      })

      return (
        <EuiFlexGroup gutterSize='xs' justifyContent='flexStart' alignItems='baseline'>
          <EuiFlexItem>
            <SizePicker
              data-test-id='topologyElement-autoscalingMaxSize'
              {...normalizeSizingProps}
              options={options}
              maxInstanceCount={
                maxInstanceCount
                  ? Math.min(maxInstanceCount, maxInstanceCountForEnvironment)
                  : maxInstanceCountForEnvironment
              }
              topologyElement={topologyElement}
              sliderInstanceType={sliderInstanceType}
              sliderNodeTypes={sliderNodeTypes}
              isBlobStorage={isBlobStorage}
              // overrides to have SizePicker get/set `autoscaling_max` instead of `size`
              size={normalizeSizingProps.autoscalingMaxSize || 0}
              onChangeSize={
                normalizeSizingProps.onChangeAutoscaling &&
                ((maxValue) => normalizeSizingProps.onChangeAutoscaling!({ maxValue }))
              }
              aria-label={ariaLabel}
            />{' '}
          </EuiFlexItem>
          <EuiFlexItem grow={false} style={{ width: '2rem' }} />
        </EuiFlexGroup>
      )
    }

    return (
      <Fragment>
        <EuiSpacer size='m' />
        <TopologyPanelRow wideLabel={true} alignItems='center' label={label}>
          <NormalizeSizing
            {...this.props}
            instanceConfiguration={instanceConfiguration}
            autoscalingSizeFilter='fixToMin'
          >
            {controlRenderer}
          </NormalizeSizing>
        </TopologyPanelRow>
      </Fragment>
    )
  }

  renderAvailabilityZones(): JSX.Element {
    const { id, topologyElement, onChange, maxZoneCount } = this.props

    // Sometimes the default set it outside what is available for certain instance configs
    const defaultZoneCount = Math.min(topologyElement.zone_count || 0, maxZoneCount)

    const instanceConfiguration = this.getCurrentInstanceConfiguration()

    const label = (
      <FormattedMessage
        id='deploymentTemplateInfrastructure-templateTopologyElement-availabilityZonesLabel'
        defaultMessage='Availability zones'
      />
    )

    return (
      <Fragment>
        <EuiSpacer size='m' />
        <TopologyPanelRow wideLabel={true} label={label}>
          <ZoneCount
            id={id}
            zoneCount={defaultZoneCount}
            maxZoneCount={maxZoneCount}
            onChange={onChange && ((value) => onChange([`zone_count`], value))}
            sliderInstanceType={instanceConfiguration.instance_type}
            sliderNodeTypes={getNodeRoles({
              topologyElement,
            })}
            isDedicatedMaster={isDedicatedMaster({ topologyElement })}
          />
        </TopologyPanelRow>
      </Fragment>
    )
  }

  renderExtraFields(): React.ReactNode {
    const { extraFields } = this.props

    if (!extraFields?.length) {
      return null
    }

    return extraFields.map((ExtraField, i) => (
      <Fragment key={`extraFields${i}`}>
        <EuiSpacer size='m' />
        <ExtraField {...this.props} />
      </Fragment>
    ))
  }

  renderSummary(): JSX.Element {
    const topologyElement = this.props.topologyElement as ElasticsearchClusterTopologyElement

    const instanceConfiguration = this.getCurrentInstanceConfiguration()

    const label = (
      <strong>
        <FormattedMessage
          id='deploymentTemplateInfrastructure-templateTopologyElement-summaryLabel'
          defaultMessage='Total (size x zone)'
        />
      </strong>
    )

    return (
      <TopologyPanelRow wideLabel={true} label={label}>
        <NormalizeSizing
          {...this.props}
          instanceConfiguration={instanceConfiguration}
          autoscalingSizeFilter='fixToMin'
        >
          {(normalizeSizingProps) => (
            <Summary
              {...normalizeSizingProps}
              topologyElement={topologyElement}
              sliderInstanceType={instanceConfiguration.instance_type}
              instanceConfiguration={instanceConfiguration}
              isMachineLearning={isDedicatedML({ topologyElement })}
              isFrozen={isFrozen({ topologyElement })}
              autoscalingMin={topologyElement.autoscaling_min?.value}
              zoneCount={topologyElement.zone_count || 1} // 1 as sane fallback, though "should" always be present
            />
          )}
        </NormalizeSizing>
      </TopologyPanelRow>
    )
  }

  renderUserSettings(): JSX.Element | undefined {
    const { deployment, topologyElement, sliderInstanceType, onChange } = this.props

    const label = (
      <FormattedMessage
        id='deploymentTemplateInfrastructure-templateTopologyElement-userSettingsLabel'
        defaultMessage='User settings'
      />
    )

    const instanceConfiguration = this.getCurrentInstanceConfiguration()

    const plan = getSliderPlan({ deployment, sliderInstanceType })

    if (!plan) {
      return // sanity
    }

    const resource = getFirstSliderCluster({ deployment, sliderInstanceType })

    if (!resource) {
      return // sanity
    }

    if (hasOnlyPlanLevelSettings(resource)) {
      return // default to plan-level settings
    }

    const settings = getSliderUserSettings({
      sliderInstanceType,
      plan,
      nodeConfiguration: topologyElement,
    })
    const defaultSettings = getDefaultUserSettings(sliderInstanceType)
    const setSettings =
      onChange &&
      ((value: string) =>
        instanceConfiguration.instance_type
          ? onChange([instanceConfiguration.instance_type, `user_settings_yaml`], value)
          : undefined)

    const {
      messages: { prettyName },
      userSettingsFileName,
    } = getSliderDefinition({ sliderInstanceType })
    const docLink = instanceConfiguration.instance_type
      ? USER_SETTINGS_DOC_LINKS[instanceConfiguration.instance_type]
      : undefined

    return (
      <Fragment>
        <EuiSpacer size='m' />
        <TopologyPanelRow wideLabel={true} label={label}>
          <EuiText>
            <UserSettings
              settings={settings == null ? defaultSettings : settings}
              onChange={setSettings}
              isReadOnly={!onChange}
              prettyName={prettyName}
              fileName={userSettingsFileName}
              docLink={docLink}
            />
          </EuiText>
        </TopologyPanelRow>
      </Fragment>
    )
  }

  getCurrentInstanceConfiguration(): InstanceConfiguration {
    const { topologyElement, instanceConfigurations, templateICs } = this.props

    if (templateICs != null) {
      const combinedICs = templateICs.concat(instanceConfigurations)
      return getInstanceConfigurationByIdAndVersion(
        combinedICs,
        topologyElement.instance_configuration_id!,
        topologyElement.instance_configuration_version!,
      )!
    }

    return getInstanceConfigurationById(
      instanceConfigurations,
      topologyElement.instance_configuration_id!,
    )!
  }
}

export default injectIntl(TemplateTopologyElement)
